/**
 * apiConfig.js
 * --------------------------------------------------------------
 * Single source of truth for every outbound base URL used by the
 * ParkAdda events website.  All API clients and legacy service
 * modules read from here, and every value is driven by a CRA env
 * variable (prefix ``REACT_APP_``) with a sensible production
 * default as the fallback.
 *
 * Precedence, from highest to lowest:
 *   1. The module-specific env var (e.g. ``REACT_APP_CART_API_BASE``).
 *      Kept for backward compatibility with existing deployments.
 *   2. The shared root (``REACT_APP_API_ROOT`` / ``REACT_APP_WS_ROOT``)
 *      combined with the canonical path for that module.
 *   3. The hardcoded production default below.
 *
 * To point the site at a different environment, set one of:
 *   REACT_APP_API_ROOT=https://staging-server.parkadda.com
 *   REACT_APP_WS_ROOT=wss://staging-server.parkadda.com
 * in your ``.env`` / ``.env.local`` / ``.env.production`` file and
 * everything re-wires automatically.
 */

const DEFAULT_API_ROOT = "https://server.parkadda.com";
const DEFAULT_WS_ROOT = "wss://server.parkadda.com";

/** Strip any trailing slash so callers can always concatenate ``"/..."``. */
const trimTrail = (u) => String(u || "").replace(/\/+$/, "");

/** HTTP(S) root used by every REST client in the site. */
export const API_ROOT = trimTrail(
  process.env.REACT_APP_API_ROOT || DEFAULT_API_ROOT
);

/** WebSocket root used by realtime streams. */
export const WS_ROOT = trimTrail(
  process.env.REACT_APP_WS_ROOT || DEFAULT_WS_ROOT
);

/** Build a URL under ``API_ROOT`` (always starts with ``/``). */
const underRoot = (suffix) =>
  API_ROOT + (suffix.startsWith("/") ? suffix : "/" + suffix);

/**
 * Per-module API bases.  Each entry respects the legacy module-specific
 * env var if present; otherwise derives from ``API_ROOT``.
 */
export const API_BASES = {
  EVENTS:
    process.env.REACT_APP_EVENTS_API_BASE || underRoot("/api/events"),
  CART:
    process.env.REACT_APP_CART_API_BASE || underRoot("/api/cart"),
  VALET:
    process.env.REACT_APP_VALET_API_BASE || underRoot("/api/valet"),
  VENDORPAY:
    process.env.REACT_APP_VENDORPAY_API_BASE || underRoot("/api/vendorpay"),
  SUPPLY:
    process.env.REACT_APP_SUPPLY_API_BASE || underRoot("/api/vendor/supply"),
  VOICE_CONSENT:
    process.env.REACT_APP_VOICE_CONSENT_API_BASE ||
    underRoot("/api/voice-consent"),
  ACCREDITATION:
    process.env.REACT_APP_ACCREDITATION_API_BASE ||
    underRoot("/api/accreditation"),
  INQUIRY_FORM:
    process.env.REACT_APP_INQUIRY_FORM_URL ||
    underRoot("/parkadda/inquiry_form/"),
  TROOPER:
    process.env.REACT_APP_TROOPER_API_BASE || underRoot("/api/trooper"),
  USER: process.env.REACT_APP_USER_API_BASE || underRoot("/api/user"),
  PARKING: process.env.REACT_APP_PARKING_API_BASE || underRoot("/api/parking"),
  BOOKING: process.env.REACT_APP_BOOKING_API_BASE || underRoot("/api/booking"),
  VEHICLE: process.env.REACT_APP_VEHICLE_API_BASE || underRoot("/api/vehicle"),
  BOOKMARK: process.env.REACT_APP_BOOKMARK_API_BASE || underRoot("/api/bookmark"),
  NOTIFICATION:
    process.env.REACT_APP_NOTIFICATION_API_BASE || underRoot("/api/notification"),
  RATING: process.env.REACT_APP_RATING_API_BASE || underRoot("/api/rating"),
  WALLET: process.env.REACT_APP_WALLET_API_BASE || underRoot("/api/wallet"),
  HOST: process.env.REACT_APP_HOST_API_BASE || underRoot("/api/host"),
  REPORTS: process.env.REACT_APP_REPORTS_API_BASE || underRoot("/api/reports"),
  RESIDENT: process.env.REACT_APP_RESIDENT_API_BASE || underRoot("/api/resident"),
  MASTER: process.env.REACT_APP_MASTER_API_BASE || underRoot("/api/master"),
};

/** Google Maps / Places key for citizen parking discovery. */
export const GOOGLE_MAPS_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_KEY ||
  "AIzaSyDBEMKqiKJ3E-zUI-QyOyfuudqo-iSswu8";

/** Map ID for Advanced Markers (create in Cloud Console or use DEMO_MAP_ID locally). */
export const GOOGLE_MAPS_MAP_ID =
  process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID";

export const CASHFREE_MODE =
  process.env.REACT_APP_CASHFREE_MODE || "production";

export default { API_ROOT, WS_ROOT, API_BASES };
